import { apiErrorMessage } from '@agos/api-client'
import {
  getGetEmployeesExternalDriversQueryKey,
  useGetEmployeesExternalDrivers,
  usePatchEmployeesIdApprove,
  usePutEmployeesIdExternalRates,
} from '@agos/api-client/hr'
import { formatCents, formatDecimal, formatMoney, T, tt } from '@agos/shared'
import { EmptyState, PageHeader, StatCard, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  Edit2,
  Euro,
  MailCheck,
  MapPin,
  Package,
  ShieldAlert,
  ShieldCheck,
  Truck,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  approvalUnblocks,
  driverGate,
  externalTotals,
  isValidRate,
  periodRange,
  projectedRewardCents,
  type ExternalDriver,
  type Period,
} from './external-driver'

/**
 * `/external-driver` — couriers who are not employees, paid per delivery.
 *
 * **§9.2 said this feature was broken, and it was.** The diagnosis is in
 * `docs/pages/manager-delivery/external-driver.md`: `is_external` had no write
 * path in either backend, and the self-registration that once set it belonged to
 * the retired driver app. So this screen managed a set that was always empty, and
 * `GET /employees/external-drivers` returned `[]` against the production dumps.
 *
 * **The write path landed 2026-08-05** — `EmployeeService` names `is_external` in
 * its `INSERT` citing §9.2, and §9.2's journey e2e seeds a driver and walks it.
 * The list is still `[]` on the restored dumps because no franchise has created
 * one, which now means *nobody has signed up yet* rather than *nobody can*. Those
 * two look identical on screen, which is why this paragraph is worth keeping
 * accurate rather than deleting.
 *
 * The screen itself is correct and is ported faithfully. Its siblings
 * `index.ts` and `external-driver.ts` carried the same stale claim and were
 * corrected on 2026-08-05; this one survived that sweep because the phrase
 * *"no write path"* wraps across a line here and a line-based grep cannot see it.
 */

const PERIODS: Array<{ id: Period; label: string }> = [
  { id: 'today', label: tt('Dnes') },
  { id: 'week', label: tt('Týždeň') },
  { id: 'month', label: tt('Mesiac') },
  { id: 'custom', label: tt('Vlastné') },
]

const FIELD_CLASSES =
  'px-2 py-1 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 tabular-nums w-20'

export function ExternalDriverPage() {
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<Period>('month')
  const [custom, setCustom] = useState({ from: '', to: '' })

  const range = useMemo(() => periodRange(period, custom, new Date()), [period, custom])
  const { data, isLoading } = useGetEmployeesExternalDrivers({ from: range.from, to: range.to })

  const drivers = useMemo(
    () => (Array.isArray(data) ? (data as ExternalDriver[]) : []),
    [data],
  )
  const totals = externalTotals(drivers)

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesExternalDriversQueryKey() })

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Externí šoféri')}
        description={tt('Kuriéri platení za rozvoz.')}
        icon={<Truck className="w-5 h-5" />}
      />

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-xl border border-border overflow-hidden">
          {PERIODS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setPeriod(option.id)}
              className={cn(
                'px-3 py-1.5 text-xs font-bold transition-colors',
                period === option.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <>
            <input
              type="date"
              value={custom.from}
              onChange={(event) => setCustom((current) => ({ ...current, from: event.target.value }))}
              aria-label={tt('Od')}
              className="px-3 py-1.5 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
            <input
              type="date"
              value={custom.to}
              onChange={(event) => setCustom((current) => ({ ...current, to: event.target.value }))}
              aria-label={tt('Do')}
              className="px-3 py-1.5 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={tt('Šoférov')} value={String(totals.driverCount)} icon={Truck} color="violet" />
        <StatCard
          label={tt('Rozvozov')}
          value={String(totals.deliveries)}
          icon={Package}
          color="blue"
        />
        <StatCard
          label={tt('Kilometrov')}
          value={`${formatDecimal(totals.kilometres, 1)} km`}
          icon={MapPin}
          color="emerald"
        />
        <StatCard
          label={tt('Na výplatu')}
          value={formatCents(totals.rewardCents)}
          icon={Euro}
          color="amber"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((placeholder) => (
            <div
              key={placeholder}
              className="h-20 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : drivers.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-10 space-y-3">
          <EmptyState
            icon={<Truck className="w-10 h-10" />}
            title={tt('Žiadni externí šoféri')}
            description={tt('V tomto období nikto z externých kuriérov nerozvážal.')}
          />
          {/* §9.2. This said the system offered no way to mark an employee external and called it a
              server-side bug. That was true when written and stopped being true when `is_external`
              gained a write path - leaving the screen telling a manager not to look for a control
              that exists two screens away. Still not "nobody has signed up": there is no
              self-registration, and marking an existing employee is a different act. */}
          <p className="text-[11px] text-muted-foreground text-center max-w-lg mx-auto">
            <T>
              Externý šofér sa označuje v Zamestnancoch - zaškrtnite „Externý vodič“ na zamestnancovi
              s rolou šofér. Prihlásiť sa ako externý šofér sám nikto nemôže.
            </T>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {drivers.map((driver) => (
            <DriverCard key={driver.id} driver={driver} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  )
}

function DriverCard({
  driver,
  onChanged,
}: {
  driver: ExternalDriver
  onChanged: () => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [ratePerOrder, setRatePerOrder] = useState(String(driver.ratePerOrder ?? 0))
  const [ratePerKm, setRatePerKm] = useState(String(driver.ratePerKm ?? 0))
  const [isSaving, setIsSaving] = useState(false)

  const gate = driverGate(driver)
  const canUnblock = approvalUnblocks(driver)
  const projected = projectedRewardCents(driver, { ratePerOrder, ratePerKm })
  const saveExternalRates = usePutEmployeesIdExternalRates()
  const setApproved = usePatchEmployeesIdApprove()

  const saveRates = async () => {
    // `driver.id` is optional on the generated model, and the hand-rolled call interpolated it
    // straight into the path — an undefined id produced `/employees/undefined/external-rates` and a
    // 404 that read as a server error. The typed hook refuses it, so it is checked here.
    if (driver.id === undefined) return
    if (!isValidRate(ratePerOrder) || !isValidRate(ratePerKm)) {
      toast({ variant: 'destructive', title: tt('Neplatná hodnota'), description: '' })
      return
    }
    setIsSaving(true)
    try {
      // The generated hook, now that the spec describes this PUT — the body is checked rather than
      // hand-assembled into a `customFetch`.
      await saveExternalRates.mutateAsync({
        id: driver.id,
        data: {
          ratePerOrder: Number(ratePerOrder.replace(',', '.')) || 0,
          ratePerKm: Number(ratePerKm.replace(',', '.')) || 0,
        },
      })
      toast({ title: tt('Sadzby uložené'), description: '' })
      setIsEditing(false)
      onChanged()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, ''),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const setApproval = async (approved: boolean) => {
    if (driver.id === undefined) return
    try {
      await setApproved.mutateAsync({ id: driver.id, data: { approved } })
      toast({
        title: approved ? tt('Šofér schválený') : tt('Schválenie odobraté'),
        description: '',
      })
      onChanged()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, ''),
      })
    }
  }

  return (
    <div className="bg-card border border-border rounded-2xl px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{driver.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {[driver.phone, driver.email].filter(Boolean).join(' · ') || '-'}
          </p>
        </div>

        <GateBadge gate={gate} />

        <span className="text-right shrink-0">
          <span className="block text-sm font-bold tabular-nums">
            {driver.deliveryCount ?? 0} <T>rozvozov</T>
          </span>
          <span className="block text-[11px] text-muted-foreground tabular-nums">
            {formatDecimal(driver.totalKm ?? 0, 1)} km
          </span>
        </span>

        <span className="text-base font-black text-amber-500 tabular-nums shrink-0">
          {formatMoney(driver.reward)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/60">
        {isEditing ? (
          <>
            <label className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground whitespace-nowrap">
                <T>€ / objednávka</T>
              </span>
              <input
                value={ratePerOrder}
                onChange={(event) => setRatePerOrder(event.target.value)}
                aria-label={tt('€ / objednávka')}
                className={FIELD_CLASSES}
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground whitespace-nowrap">€ / km</span>
              <input
                value={ratePerKm}
                onChange={(event) => setRatePerKm(event.target.value)}
                aria-label="€ / km"
                className={FIELD_CLASSES}
              />
            </label>
            {/* The consequence before the save, computed the way the server will
                compute it - a manager should not approve a number and be paid a
                different one. */}
            <span className="text-xs text-muted-foreground">
              → <span className="font-bold text-amber-500">{formatCents(projected)}</span>
            </span>
            <button
              type="button"
              onClick={() => void saveRates()}
              disabled={isSaving}
              aria-label={tt('Uložiť sadzby')}
              className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              aria-label={tt('Zrušiť')}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Euro className="w-3.5 h-3.5" />
              {formatMoney(driver.ratePerOrder)} <T>/ objednávka</T>
            </span>
            <span className="text-border">·</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" />
              {formatMoney(driver.ratePerKm)} / km
            </span>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              aria-label={`${tt('Upraviť sadzby')} - ${driver.name ?? ''}`}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => void setApproval(driver.adminApproved !== true)}
          title={
            canUnblock
              ? tt('Schválením sa šofér bude môcť prihlásiť.')
              : gate === 'blocked' || gate === 'awaiting-email'
                ? tt('Šofér ešte nepotvrdil email - schválenie ho zatiaľ nepustí dnu.')
                : undefined
          }
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors',
            driver.adminApproved
              ? 'border-border text-muted-foreground hover:text-foreground'
              : 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/25',
          )}
        >
          {driver.adminApproved ? (
            <>
              <ShieldAlert className="w-3.5 h-3.5" /> <T>Odobrať schválenie</T>
            </>
          ) : (
            <>
              <ShieldCheck className="w-3.5 h-3.5" /> <T>Schváliť</T>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

const GATE_LABELS: Record<string, { label: string; className: string; icon: typeof MailCheck }> = {
  ready: {
    label: tt('môže sa prihlásiť'),
    className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25',
    icon: ShieldCheck,
  },
  'awaiting-approval': {
    label: tt('čaká na schválenie'),
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/25',
    icon: ShieldAlert,
  },
  'awaiting-email': {
    label: tt('nepotvrdil email'),
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/25',
    icon: MailCheck,
  },
  blocked: {
    label: tt('nepotvrdil email ani schválenie'),
    className: 'bg-red-500/10 text-red-600 border-red-500/25',
    icon: ShieldAlert,
  },
}

function GateBadge({ gate }: { gate: string }) {
  const style = GATE_LABELS[gate] ?? GATE_LABELS.blocked
  if (!style) return null
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold shrink-0',
        style.className,
      )}
    >
      <style.icon className="w-3 h-3" />
      {style.label}
    </span>
  )
}
